import { analyticsTrack } from "../../../../helpers/analytics";

const buildLinkHandler = (hrefBuilder) => ({
  type: "link",
  hrefBuilder
});

const buildCallbackHandler = (resolve) => ({
  type: "callback",
  resolve
});

const buildPageAwareHandler = (resolve) => ({
  type: "pageAware",
  resolve
});

const buildPdfReportHandler = () => ({ type: "pdf_report" });

const UPGRADE_PATH = "/explorer/upgrade";
const ACLYMATE_DEMO_URL = "https://aclymate.com/demo";

const isOnUpgradePage = (pathname) => pathname === UPGRADE_PATH;

const buildUpgradeLink = (payload, fallbackAnchor) => {
  const anchor = payload?.anchor || fallbackAnchor;
  return UPGRADE_PATH + (anchor ? "#" + anchor : "");
};

export const SHARE_ACTION_TYPES = new Set([
  "share_customer_year",
  "open_supplier_share_review",
  "start_bom_survey",
  "take_emissions_survey",
  "open_product_request"
]);

export const ACTION_HANDLERS = {
  talk_to_a_human: buildCallbackHandler(({ openSupport }) => openSupport),
  talk_to_expert: buildCallbackHandler(
    ({ openExpertMeeting }) => openExpertMeeting
  ),
  estimate_to_measure: buildLinkHandler((payload) =>
    buildUpgradeLink(payload, "navigator")
  ),
  upgrade_to_navigator: buildLinkHandler((payload) =>
    buildUpgradeLink(payload, "navigator")
  ),
  upgrade_to_aclymate_one: buildLinkHandler((payload) =>
    buildUpgradeLink(payload, "aclymate-one")
  ),
  upgrade_to_aclymate_custom: buildLinkHandler((payload) =>
    buildUpgradeLink(payload, "aclymate-custom")
  ),
  start_navigator_trial: buildPageAwareHandler(
    (_deps, _payload, { pathname }) => {
      if (isOnUpgradePage(pathname)) {
        return { type: "link", href: "/account-creation/company" };
      }
      return { type: "link", href: buildUpgradeLink({}, "navigator") };
    }
  ),
  book_demo: buildPageAwareHandler(
    ({ openExpertMeeting }, _payload, { pathname }) => {
      if (isOnUpgradePage(pathname) && openExpertMeeting) {
        return { type: "callback", fn: openExpertMeeting };
      }
      return { type: "link", href: buildUpgradeLink({}, "aclymate-one") };
    }
  ),
  see_demo: buildPageAwareHandler((_deps, _payload, { pathname }) => {
    if (isOnUpgradePage(pathname)) {
      return { type: "link", href: ACLYMATE_DEMO_URL };
    }
    return { type: "link", href: buildUpgradeLink({}, "aclymate-one") };
  }),
  talk_to_sales: buildPageAwareHandler(
    ({ openExpertMeeting }, _payload, { pathname }) => {
      if (isOnUpgradePage(pathname) && openExpertMeeting) {
        return { type: "callback", fn: openExpertMeeting };
      }
      return { type: "link", href: buildUpgradeLink({}, "aclymate-custom") };
    }
  ),
  open_customer_request: buildLinkHandler(
    (payload) =>
      `/explorer/dashboard?view=customers&relationshipId=${encodeURIComponent(
        payload?.relationshipId || ""
      )}`
  ),
  open_product_request: buildLinkHandler(
    (payload) =>
      `/explorer/bom-survey?lifecycleEventId=${encodeURIComponent(
        payload?.productRequestId || ""
      )}`
  ),
  take_emissions_survey: buildLinkHandler((payload) =>
    payload?.year
      ? `/explorer/emissions-survey?year=${encodeURIComponent(payload.year)}`
      : "/explorer/emissions-survey"
  ),
  resume_emissions_survey: buildLinkHandler(
    () => "/explorer/emissions-survey?resume=1"
  ),
  start_bom_survey: buildLinkHandler(
    (payload) =>
      `/explorer/bom-survey?lifecycleEventId=${encodeURIComponent(
        payload?.lifecycleEventId || ""
      )}`
  ),
  navigate: buildLinkHandler((payload) => payload?.href || "/"),
  share_customer_year: buildCallbackHandler(
    ({ shareCustomerYear }, payload) =>
      () => {
        if (!shareCustomerYear) {
          return;
        }
        shareCustomerYear({
          relationshipId: payload?.relationshipId,
          year: payload?.year
        });
      }
  ),
  acknowledge_customer_pending: buildCallbackHandler((_deps, payload) => () => {
    analyticsTrack("scout_customer_pending_acknowledged", {
      relationshipId: payload?.relationshipId,
      year: payload?.year
    });
  }),
  open_supplier_share_review: buildCallbackHandler(
    ({ acceptSupplierShare }, payload) =>
      () => {
        if (!acceptSupplierShare) {
          return;
        }
        acceptSupplierShare({
          relationshipId: payload?.relationshipId,
          year: payload?.year
        });
      }
  ),
  download_pdf_report: buildPdfReportHandler()
};

export const dispatchActionHandler = ({ actionType, payload, deps }) => {
  const handler = ACTION_HANDLERS[actionType];
  if (!handler) {
    return false;
  }
  if (handler.type === "link") {
    const href = handler.hrefBuilder(payload || {});
    window.location.href = href;
    return true;
  }
  if (handler.type === "pageAware") {
    const ctx = {
      pathname:
        typeof window !== "undefined" && window.location
          ? window.location.pathname
          : ""
    };
    const resolved = handler.resolve(deps || {}, payload || {}, ctx);
    if (resolved && resolved.type === "link") {
      window.location.href = resolved.href;
      return true;
    }
    if (
      resolved &&
      resolved.type === "callback" &&
      typeof resolved.fn === "function"
    ) {
      resolved.fn();
      return true;
    }
    return false;
  }
  if (handler.type === "callback") {
    const callback = handler.resolve(deps || {}, payload || {});
    if (typeof callback === "function") {
      callback();
    }
    return true;
  }
  return false;
};
